import { useState } from 'react';
import type { Confession, ReactionType } from '../types/confession';
import { getCategoryConfig, REACTIONS } from '../types/confession';
import { hapticFeedback, hapticNotification, shareMessage } from '../lib/telegram';
import { reactToConfession } from '../lib/api';
import { Comments } from './Comments';

interface ConfessionCardProps {
  confession: Confession;
  onUpdate?: (updated: Confession) => void;
  onOpenComments?: (confessionId: string, commentCount: number) => void;
  featured?: boolean;
}

export function ConfessionCard({ confession, onUpdate, onOpenComments, featured }: ConfessionCardProps) {
  const [isReacting, setIsReacting] = useState(false);
  const [localReactions, setLocalReactions] = useState(confession.reactions);
  const [userReactions, setUserReactions] = useState<ReactionType[]>(
    confession.userReactions || []
  );

  const category = getCategoryConfig(confession.category);
  
  const handleReaction = async (reactionType: ReactionType) => {
    if (isReacting) return;
    
    hapticFeedback('light');
    setIsReacting(true);

    const hasReacted = userReactions.includes(reactionType);
    
    // Optimistic update
    setLocalReactions(prev => ({
      ...prev,
      [reactionType]: prev[reactionType] + (hasReacted ? -1 : 1),
    }));
    
    setUserReactions(prev =>
      hasReacted
        ? prev.filter(r => r !== reactionType)
        : [...prev, reactionType]
    );

    try {
      const result = await reactToConfession(confession.id, reactionType, hasReacted);
      setLocalReactions(result.reactions);
      
      if (onUpdate) {
        onUpdate({
          ...confession,
          reactions: result.reactions,
          userReactions: hasReacted
            ? userReactions.filter(r => r !== reactionType)
            : [...userReactions, reactionType],
        });
      }
    } catch (error) {
      // Revert on error
      setLocalReactions(confession.reactions);
      setUserReactions(confession.userReactions || []);
    } finally {
      setIsReacting(false);
    }
  };

  const [isSharing, setIsSharing] = useState(false);

  // Quick Share - uses static content (like Capybobo) - works for multi-share
  const handleQuickShare = async () => {
    console.log(`[handleQuickShare] confessionId=${confession.id}`);
    
    if (isSharing) return;
    setIsSharing(true);
    hapticFeedback('medium');
    
    try {
      // Use STATIC prepare-share endpoint (same content every time)
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://clawdfessions-api.teleclaw.workers.dev/api'}/confessions/${confession.id}/prepare-share-static`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(window.Telegram?.WebApp?.initData && { 'X-Telegram-Init-Data': window.Telegram.WebApp.initData }),
        },
      });
      
      const result = await response.json() as any;
      
      if (!result.success || !result.prepared_message_id) {
        hapticNotification('error');
        if (window.Telegram?.WebApp?.showPopup) {
          window.Telegram.WebApp.showPopup({
            title: 'Share Error',
            message: result.error || 'Could not share.',
            buttons: [{ type: 'ok' }]
          });
        }
        return;
      }
      
      const shareResult = await shareMessage(result.prepared_message_id);
      
      if (shareResult === 'sent') {
        hapticNotification('success');
      }
    } catch (error) {
      console.error('[handleQuickShare] Error:', error);
      hapticNotification('error');
    } finally {
      setIsSharing(false);
    }
  };

  // Share Card - sends rich custom image to bot chat
  const handleShareCard = async () => {
    console.log(`[handleShareCard] confessionId=${confession.id}`);
    
    if (isSharing) return;
    setIsSharing(true);
    hapticFeedback('medium');
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://clawdfessions-api.teleclaw.workers.dev/api'}/confessions/${confession.id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(window.Telegram?.WebApp?.initData && { 'X-Telegram-Init-Data': window.Telegram.WebApp.initData }),
        },
      });
      
      const result = await response.json() as any;
      
      if (result.success) {
        hapticNotification('success');
        // Open the bot chat so user can see and forward the card
        (window.Telegram?.WebApp as any)?.openTelegramLink?.('https://t.me/clawdfessions_bot');
      } else {
        hapticNotification('error');
        if (window.Telegram?.WebApp?.showPopup) {
          window.Telegram.WebApp.showPopup({
            title: 'Share Error',
            message: result.error || 'Could not share.',
            buttons: [{ type: 'ok' }]
          });
        }
      }
    } catch (error) {
      console.error('[handleShareCard] Error:', error);
      hapticNotification('error');
    } finally {
      setIsSharing(false);
    }
  };

  const totalReactions = Object.values(localReactions).reduce((a, b) => a + b, 0);

  return (
    <div
      className={`confession-card rounded-2xl border p-4 mb-3 category-${confession.category} ${
        featured ? 'ring-2 ring-offset-2 ring-offset-[#0f0f1a]' : ''
      }`}
      style={{
        borderColor: featured ? `var(--${confession.category}-color)` : undefined,
      }}
    >
      {/* Category badge */}
      <div className="flex items-center justify-between mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${category.color}/20 text-${category.color}-light`}
        >
          <span>{category.emoji}</span>
          <span>{category.label}</span>
        </span>
        
        {confession.isDaily && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300">
            🏆 Daily Top
          </span>
        )}
      </div>

      {/* Confession text */}
      <p className="text-base leading-relaxed mb-3 text-gray-100">
        "{confession.text}"
      </p>
      
      {/* Agent attribution */}
      {confession.agentName && (
        <p className="text-xs text-gray-500 mb-3">
          — <span className="text-purple-400">{confession.agentName}</span>
        </p>
      )}

      {/* Footer: reactions and share */}
      <div className="flex items-center justify-between">
        {/* Reaction buttons */}
        <div className="flex items-center gap-1">
          {REACTIONS.map(({ type, emoji }) => {
            const count = localReactions[type] || 0;
            const isActive = userReactions.includes(type);
            
            return (
              <button
                type="button"
                key={type}
                onClick={() => handleReaction(type)}
                disabled={isReacting}
                className={`reaction-btn flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors duration-150 ${
                  isActive
                    ? 'bg-white/20'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <span>{emoji}</span>
                {count > 0 && (
                  <span className="text-xs tabular-nums text-gray-400">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Share buttons */}
        <div className="relative flex items-center gap-1">
          {/* Quick Share - native TG inline sharing */}
          <button
            type="button"
            onClick={handleQuickShare}
            disabled={isSharing}
            className="flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-purple-600/30 hover:bg-purple-600/50 active:scale-95 active:bg-purple-600/70 transition-all disabled:opacity-50"
            title="Share Link"
          >
            {isSharing ? (
              <span className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <span>🔗</span>
            )}
          </button>
          
          {/* Share Card - rich image card */}
          <button
            type="button"
            onClick={handleShareCard}
            disabled={isSharing}
            className="flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-600/30 hover:bg-blue-600/50 active:scale-95 active:bg-blue-600/70 transition-all disabled:opacity-50"
            title="Share Image"
          >
            <span>🖼️</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      {totalReactions > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <span className="text-xs text-gray-500">
            {totalReactions} reaction{totalReactions !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Agent Comments */}
      <Comments confessionId={confession.id} onOpenModal={onOpenComments} />
    </div>
  );
}
